import { useEffect, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import DealLink from "../DealLink";
import { daysInStage, daysInPhase, daysSinceCreated } from "../../lib/dealAge";
import { formatPhone } from "../../lib/phoneFormat";
import { mapsHref } from "../../lib/mapsHref";
// Outreach board is hard-scoped to `insufficient_damage` stage in the query —
// not a CLOSED stage per the canonical filter, so all rows here are linkable.

/** Joy's Insufficient Damage Outreach Board.
 *  Shows deals in the `insufficient_damage` stage that need homeowner outreach.
 *  Joy can mark each one contacted, add notes, and track the outreach status. */

interface OutreachDeal {
  id: string;
  homeowner_name: string | null;
  homeowner_phone: string | null;
  homeowner_email: string | null;
  property_street: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  claim_number: string | null;
  insurance_company: string | null;
  owner_id: string | null;
  days_in_stage: number | null;
  stage_entered_at: string | null;
  phase_entered_at: string | null;
  created_at: string | null;
  canvasser_name: string | null;
}

type OutreachStatus = "pending" | "contacted" | "completed";

interface OutreachState {
  status: OutreachStatus;
  note: string;
}

export default function JoyOutreachBoard() {
  const { member } = useAuth();
  const [deals, setDeals] = useState<OutreachDeal[] | null>(null);
  const [members, setMembers] = useState<Record<string, string>>({});
  const [outreachState, setOutreachState] = useState<Record<string, OutreachState>>({});
  const [filter, setFilter] = useState<"all" | "pending" | "contacted" | "completed">("all");
  const [saving, setSaving] = useState<string | null>(null);

  // Fetch team members for RM name resolution
  useEffect(() => {
    supabase
      .from("team_roster")
      .select("id,full_name")
      .eq("is_active", true)
      .then(({ data }) => {
        const map: Record<string, string> = {};
        (data ?? []).forEach((m) => { map[m.id] = m.full_name; });
        setMembers(map);
      });
  }, []);

  // Fetch insufficient_damage deals
  const loadDeals = useCallback(async () => {
    const { data } = await supabase
      .from("deals")
      .select(
        "id,homeowner_name,homeowner_phone,homeowner_email,property_street,property_city,property_state,property_zip,claim_number,insurance_company,owner_id,days_in_stage,stage_entered_at,phase_entered_at,created_at,canvasser_name"
      )
      .eq("stage", "insufficient_damage")
      .order("created_at", { ascending: false });

    if (data) {
      setDeals(data as OutreachDeal[]);
      // Load existing outreach activity_log entries for these deals
      const dealIds = data.map((d) => d.id);
      if (dealIds.length > 0) {
        const { data: logs } = await supabase
          .from("activity_log")
          .select("deal_id,action,note")
          .in("deal_id", dealIds)
          .in("action", ["insufficient_damage_contacted", "insufficient_damage_completed"]);

        const stateMap: Record<string, OutreachState> = {};
        (logs ?? []).forEach((log) => {
          const existing = stateMap[log.deal_id];
          if (log.action === "insufficient_damage_completed") {
            stateMap[log.deal_id] = { status: "completed", note: log.note ?? "" };
          } else if (log.action === "insufficient_damage_contacted" && (!existing || existing.status !== "completed")) {
            stateMap[log.deal_id] = { status: "contacted", note: log.note ?? "" };
          }
        });
        setOutreachState(stateMap);
      }
    }
  }, []);

  useEffect(() => { loadDeals(); }, [loadDeals]);

  const getStatus = (dealId: string): OutreachStatus =>
    outreachState[dealId]?.status ?? "pending";

  const markContacted = async (dealId: string, note: string) => {
    setSaving(dealId);
    await supabase.from("activity_log").insert({
      deal_id: dealId,
      action: "insufficient_damage_contacted",
      performed_by: member?.id ?? null,
      performed_by_name: member?.full_name ?? "Joy",
      note: note || "Homeowner contacted about insufficient damage finding.",
    });
    setOutreachState((prev) => ({
      ...prev,
      [dealId]: { status: "contacted", note },
    }));
    setSaving(null);
  };

  const markCompleted = async (dealId: string, note: string) => {
    setSaving(dealId);
    await supabase.from("activity_log").insert({
      deal_id: dealId,
      action: "insufficient_damage_completed",
      performed_by: member?.id ?? null,
      performed_by_name: member?.full_name ?? "Joy",
      note: note || "Outreach completed.",
    });
    setOutreachState((prev) => ({
      ...prev,
      [dealId]: { status: "completed", note },
    }));
    setSaving(null);
  };

  const filteredDeals = (deals ?? []).filter((d) => {
    if (filter === "all") return true;
    return getStatus(d.id) === filter;
  });

  const pendingCount = (deals ?? []).filter((d) => getStatus(d.id) === "pending").length;
  const contactedCount = (deals ?? []).filter((d) => getStatus(d.id) === "contacted").length;
  const completedCount = (deals ?? []).filter((d) => getStatus(d.id) === "completed").length;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-bardin-ink">
          Insufficient Damage Outreach
        </h1>
        <p className="text-sm text-slate-500">
          Contact homeowners whose properties had insufficient damage on drone inspection.
        </p>
      </div>

      {/* Filter badges */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {(
          [
            { key: "all", label: "All", count: deals?.length ?? 0 },
            { key: "pending", label: "Needs Outreach", count: pendingCount },
            { key: "contacted", label: "Contacted", count: contactedCount },
            { key: "completed", label: "Completed", count: completedCount },
          ] as const
        ).map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={
              "px-3 py-1.5 rounded-full text-sm font-medium transition " +
              (filter === f.key
                ? "bg-bardin-teal text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200")
            }
          >
            {f.label}
            <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded-full text-xs">
              {f.count}
            </span>
          </button>
        ))}
      </div>

      {deals === null && (
        <p className="text-sm text-slate-400 py-8 text-center">Loading...</p>
      )}

      {deals !== null && filteredDeals.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <p className="text-lg mb-1">No deals to show</p>
          <p className="text-sm">
            {filter === "all"
              ? "No insufficient damage deals right now."
              : `No ${filter} deals.`}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {filteredDeals.map((deal) => (
          <OutreachCard
            key={deal.id}
            deal={deal}
            rmName={deal.owner_id ? members[deal.owner_id] ?? "—" : "—"}
            status={getStatus(deal.id)}
            saving={saving === deal.id}
            onContact={(note) => markContacted(deal.id, note)}
            onComplete={(note) => markCompleted(deal.id, note)}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Individual outreach card
// ---------------------------------------------------------------------------

function OutreachCard({
  deal,
  rmName,
  status,
  saving,
  onContact,
  onComplete,
}: {
  deal: OutreachDeal;
  rmName: string;
  status: OutreachStatus;
  saving: boolean;
  onContact: (note: string) => void;
  onComplete: (note: string) => void;
}) {
  const [note, setNote] = useState("");
  const [expanded, setExpanded] = useState(false);

  const statusColors: Record<OutreachStatus, string> = {
    pending: "bg-amber-100 text-amber-800",
    contacted: "bg-blue-100 text-blue-800",
    completed: "bg-emerald-100 text-emerald-800",
  };

  const statusLabels: Record<OutreachStatus, string> = {
    pending: "Needs Outreach",
    contacted: "Contacted",
    completed: "Complete",
  };

  const fullAddress = [
    deal.property_street,
    deal.property_city,
    deal.property_state,
    deal.property_zip,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <DealLink id={deal.id}>
              <span className="font-medium text-bardin-ink hover:text-bardin-teal transition">
                {deal.homeowner_name ?? "Unknown"}
              </span>
            </DealLink>
            <span
              className={
                "text-xs font-medium px-2 py-0.5 rounded-full " +
                statusColors[status]
              }
            >
              {statusLabels[status]}
            </span>
            {daysInStage(deal) > 3 && (
              <span className="text-xs text-red-600 font-medium">
                {daysInStage(deal)}d in stage
              </span>
            )}
            {daysSinceCreated(deal) != null && (
              <span className="text-xs text-slate-400">{daysInPhase(deal) ?? "?"}d in phase · {daysSinceCreated(deal)}d since created</span>
            )}
          </div>
          <p className="text-sm text-slate-600 truncate">
            {deal.property_street ? (
              <a
                href={mapsHref(deal)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="hover:underline hover:text-slate-800"
              >
                {fullAddress}
              </a>
            ) : (
              "No address"
            )}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-500">
            {deal.homeowner_phone && (
              <span>
                <a
                  href={`tel:${deal.homeowner_phone}`}
                  className="text-bardin-teal hover:underline"
                >
                  {formatPhone(deal.homeowner_phone)}
                </a>
              </span>
            )}
            {deal.homeowner_email && (
              <span>
                <a
                  href={`mailto:${deal.homeowner_email}`}
                  className="text-bardin-teal hover:underline"
                >
                  {deal.homeowner_email}
                </a>
              </span>
            )}
            {deal.insurance_company && (
              <span>Insurance: {deal.insurance_company}</span>
            )}
            <span>RM: {rmName}</span>
            {deal.canvasser_name && <span>Canvasser: {deal.canvasser_name}</span>}
          </div>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="text-slate-400 hover:text-slate-600 text-sm px-2 py-1"
        >
          {expanded ? "▲" : "▼"}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3 bg-slate-50">
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note about the outreach..."
            className="w-full border border-slate-200 rounded px-3 py-2 text-sm mb-2 focus:ring-1 focus:ring-bardin-teal focus:border-bardin-teal"
          />
          <div className="flex gap-2">
            {status === "pending" && (
              <button
                onClick={() => onContact(note)}
                disabled={saving}
                className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {saving ? "Saving..." : "Mark Contacted"}
              </button>
            )}
            {(status === "pending" || status === "contacted") && (
              <button
                onClick={() => onComplete(note)}
                disabled={saving}
                className="px-4 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded hover:bg-emerald-700 disabled:opacity-50 transition"
              >
                {saving ? "Saving..." : "Mark Complete"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
